import { useState, useEffect, useRef, useCallback } from 'react';

const STORAGE_KEY_MS = 'neev_active_ms';
const STORAGE_KEY_LAST = 'neev_last_activity';

const THROTTLE_MS = 10_000;        // 10 seconds between tracked events
const IDLE_THRESHOLD_MS = 120_000; // 2 minutes without activity = idle
const IDLE_CHECK_INTERVAL = 30_000; // check for idle every 30 seconds
const EYE_REMINDER_MS = 20 * 60 * 1000; // 20 minutes
const PAUSE_SUGGESTION_MS = 30 * 60 * 1000; // 30 minutes
const EYE_COUNTDOWN_SECONDS = 20;

function readStoredMs() {
  try {
    return parseInt(sessionStorage.getItem(STORAGE_KEY_MS), 10) || 0;
  } catch {
    return 0;
  }
}

function readLastActivity() {
  try {
    return parseInt(sessionStorage.getItem(STORAGE_KEY_LAST), 10) || 0;
  } catch {
    return 0;
  }
}

function writeStoredMs(ms) {
  try {
    sessionStorage.setItem(STORAGE_KEY_MS, String(ms));
  } catch { /* quota exceeded — silently ignore */ }
}

function writeLastActivity(ts) {
  try {
    sessionStorage.setItem(STORAGE_KEY_LAST, String(ts));
  } catch { /* silently ignore */ }
}

export default function useActivityTracker() {
  const [showEyeReminder, setShowEyeReminder] = useState(false);
  const [showPauseSuggestion, setShowPauseSuggestion] = useState(false);
  const [eyeCountdown, setEyeCountdown] = useState(EYE_COUNTDOWN_SECONDS);
  const [activeMinutes, setActiveMinutes] = useState(() => Math.round(readStoredMs() / 60_000));

  // Accumulated active milliseconds (source of truth during this mount)
  const activeMsRef = useRef(readStoredMs());
  // Timestamp of last tracked interaction
  const lastActivityRef = useRef(readLastActivity() || Date.now());
  // Throttle: timestamp of last accepted event
  const lastEventTimeRef = useRef(0);
  // Whether the tab is currently visible
  const tabVisibleRef = useRef(!document.hidden);
  // Interval id for idle checking
  const idleIntervalRef = useRef(null);
  // Interval id for eye countdown
  const eyeCountdownIntervalRef = useRef(null);
  // Track whether thresholds have already fired (to fire only once per cycle)
  const eyeTriggeredRef = useRef(activeMsRef.current >= EYE_REMINDER_MS);
  const pauseTriggeredRef = useRef(activeMsRef.current >= PAUSE_SUGGESTION_MS);

  // ---------------------------------------------------------------------------
  // Persist active time and update displayed minutes
  // ---------------------------------------------------------------------------
  const persistAndUpdate = useCallback(() => {
    writeStoredMs(activeMsRef.current);
    setActiveMinutes(Math.round(activeMsRef.current / 60_000));
  }, []);

  // ---------------------------------------------------------------------------
  // Check thresholds and trigger reminders
  // ---------------------------------------------------------------------------
  const checkThresholds = useCallback(() => {
    const ms = activeMsRef.current;

    // Eye reminder at 20 min
    if (ms >= EYE_REMINDER_MS && !eyeTriggeredRef.current) {
      eyeTriggeredRef.current = true;
      setShowEyeReminder(true);
      setEyeCountdown(EYE_COUNTDOWN_SECONDS);

      // Start countdown
      if (eyeCountdownIntervalRef.current) {
        clearInterval(eyeCountdownIntervalRef.current);
      }
      let remaining = EYE_COUNTDOWN_SECONDS;
      eyeCountdownIntervalRef.current = setInterval(() => {
        remaining -= 1;
        setEyeCountdown(remaining);
        if (remaining <= 0) {
          clearInterval(eyeCountdownIntervalRef.current);
          eyeCountdownIntervalRef.current = null;
          setShowEyeReminder(false);
        }
      }, 1000);
    }

    // Pause suggestion at 30 min
    if (ms >= PAUSE_SUGGESTION_MS && !pauseTriggeredRef.current) {
      pauseTriggeredRef.current = true;
      setShowPauseSuggestion(true);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Record activity: accumulate time since last event, update last-activity
  // ---------------------------------------------------------------------------
  const recordActivity = useCallback(() => {
    const now = Date.now();
    const last = lastActivityRef.current;
    const elapsed = now - last;

    // Only accumulate if elapsed time is reasonable (not idle)
    if (elapsed > 0 && elapsed <= IDLE_THRESHOLD_MS) {
      activeMsRef.current += elapsed;
    }

    lastActivityRef.current = now;
    writeLastActivity(now);
    persistAndUpdate();
    checkThresholds();
  }, [persistAndUpdate, checkThresholds]);

  // ---------------------------------------------------------------------------
  // Throttled event handler
  // ---------------------------------------------------------------------------
  const handleUserEvent = useCallback(() => {
    if (!tabVisibleRef.current) return;

    const now = Date.now();
    if (now - lastEventTimeRef.current < THROTTLE_MS) return;
    lastEventTimeRef.current = now;

    recordActivity();
  }, [recordActivity]);

  // ---------------------------------------------------------------------------
  // Visibility change handler
  // ---------------------------------------------------------------------------
  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      // Tab going hidden — save current state but stop accumulating
      tabVisibleRef.current = false;
      writeStoredMs(activeMsRef.current);
      writeLastActivity(Date.now());
    } else {
      // Tab becoming visible — reset last activity to now so we don't
      // accumulate the hidden period
      tabVisibleRef.current = true;
      lastActivityRef.current = Date.now();
      writeLastActivity(Date.now());
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Idle check: runs every 30s to see if user went idle while tab is visible
  // ---------------------------------------------------------------------------
  const runIdleCheck = useCallback(() => {
    if (!tabVisibleRef.current) return;

    const now = Date.now();
    const sinceLastActivity = now - lastActivityRef.current;

    // If idle for >2 min, push lastActivity forward so we don't accumulate
    // the idle period on the next real interaction
    if (sinceLastActivity > IDLE_THRESHOLD_MS) {
      lastActivityRef.current = now;
      writeLastActivity(now);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Reset tracker
  // ---------------------------------------------------------------------------
  const resetTracker = useCallback(() => {
    activeMsRef.current = 0;
    lastActivityRef.current = Date.now();
    eyeTriggeredRef.current = false;
    pauseTriggeredRef.current = false;

    writeStoredMs(0);
    writeLastActivity(Date.now());
    setActiveMinutes(0);
    setShowEyeReminder(false);
    setShowPauseSuggestion(false);
    setEyeCountdown(EYE_COUNTDOWN_SECONDS);

    if (eyeCountdownIntervalRef.current) {
      clearInterval(eyeCountdownIntervalRef.current);
      eyeCountdownIntervalRef.current = null;
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Dismiss handlers
  // ---------------------------------------------------------------------------
  const dismissEyeReminder = useCallback(() => {
    setShowEyeReminder(false);
    if (eyeCountdownIntervalRef.current) {
      clearInterval(eyeCountdownIntervalRef.current);
      eyeCountdownIntervalRef.current = null;
    }
  }, []);

  const dismissPauseSuggestion = useCallback(() => {
    setShowPauseSuggestion(false);
    resetTracker();
  }, [resetTracker]);

  // ---------------------------------------------------------------------------
  // Mount / unmount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    // If there's stored last-activity and it's fresh enough, keep accumulated
    // time. Otherwise reset lastActivity to now to avoid a stale gap.
    const storedLast = readLastActivity();
    if (!storedLast || Date.now() - storedLast > IDLE_THRESHOLD_MS) {
      lastActivityRef.current = Date.now();
      writeLastActivity(Date.now());
    }

    // Register interaction listeners
    events.forEach((evt) => {
      window.addEventListener(evt, handleUserEvent, { passive: true });
    });

    // Register visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Start idle check interval
    idleIntervalRef.current = setInterval(runIdleCheck, IDLE_CHECK_INTERVAL);

    // Check thresholds on mount in case we already crossed them from stored time
    checkThresholds();

    return () => {
      events.forEach((evt) => {
        window.removeEventListener(evt, handleUserEvent);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (idleIntervalRef.current) {
        clearInterval(idleIntervalRef.current);
      }
      if (eyeCountdownIntervalRef.current) {
        clearInterval(eyeCountdownIntervalRef.current);
      }

      // Persist on unmount
      writeStoredMs(activeMsRef.current);
      writeLastActivity(Date.now());
    };
  }, [handleUserEvent, handleVisibilityChange, runIdleCheck, checkThresholds]);

  return {
    activeMinutes,
    resetTracker,
    showEyeReminder,
    showPauseSuggestion,
    dismissEyeReminder,
    dismissPauseSuggestion,
    eyeCountdown,
  };
}
